import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { StatusPill } from "./AdminDashboard";
import { AdminPhoneActions } from "../../components/AdminPhoneActions";
import { EmailCustomerDialog } from "../../lib/admin-helpers";
import { formatPhoneForDisplay } from "../../lib/phone";

const SERVICE_COLOR = {
  rental: "bg-sky-100 text-sky-900 border-sky-200",
  tours: "bg-emerald-100 text-emerald-900 border-emerald-200",
  transport: "bg-amber-100 text-amber-900 border-amber-200",
  long_term: "bg-slate-200 text-slate-900 border-slate-300",
  custom: "bg-violet-100 text-violet-900 border-violet-200",
};

export default function AdminCalendar() {
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [selected, setSelected] = useState(null); // booking detail modal
  const [emailTarget, setEmailTarget] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [view, setView] = useState("month"); // 'month' | 'week'

  const load = async () => {
    const [b, v] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/vehicles")]);
    setBookings(b.data);
    setVehicles(v.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return vehicleFilter === "all"
      ? bookings
      : bookings.filter((b) => b.vehicle_id === vehicleFilter);
  }, [bookings, vehicleFilter]);

  const grid = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const byDay = useMemo(() => {
    const map = new Map();
    for (const b of filtered) {
      const s = new Date(b.start_at);
      const e = new Date(b.end_at);
      // Add entry to every day the booking overlaps (within visible grid)
      for (const cell of grid.days) {
        if (cell.date >= stripTime(s) && cell.date <= stripTime(e)) {
          const key = dayKey(cell.date);
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(b);
        }
      }
    }
    return map;
  }, [filtered, grid]);

  const vname = (id) => vehicles.find((v) => v.id === id)?.name || "Unassigned";
  const headerLabel = view === "week" ? weekRangeLabel(cursor) : cursor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const shiftPrev = () => setCursor(view === "week" ? addDays(cursor, -7) : addMonths(cursor, -1));
  const shiftNext = () => setCursor(view === "week" ? addDays(cursor, 7) : addMonths(cursor, 1));
  const goToday = () => setCursor(view === "week" ? startOfWeek(new Date()) : startOfMonth(new Date()));

  const onDropOnDay = async (targetDate, e) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = dragId || e.dataTransfer?.getData("text/plain");
    setDragId(null);
    if (!id) return;
    const b = bookings.find((x) => x.id === id);
    if (!b) return;
    const oldStart = stripTime(new Date(b.start_at));
    const targetStart = stripTime(targetDate);
    const deltaMs = targetStart.getTime() - oldStart.getTime();
    if (deltaMs === 0) return;
    const newStart = new Date(new Date(b.start_at).getTime() + deltaMs);
    const newEnd = new Date(new Date(b.end_at).getTime() + deltaMs);
    // Optimistic UI
    setBookings((prev) => prev.map((x) => (x.id === id ? { ...x, start_at: newStart.toISOString(), end_at: newEnd.toISOString() } : x)));
    try {
      await api.put(`/admin/bookings/${id}`, {
        start_at: newStart.toISOString(),
        end_at: newEnd.toISOString(),
      });
      toast.success(`Rescheduled ${b.customer_name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reschedule");
      load(); // revert from server
    }
  };

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Calendar</h1>
          <p className="text-sm text-slate-500">Bookings per vehicle — drag a chip to reschedule.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* View switcher */}
          <div className="inline-flex rounded-md border border-slate-300 bg-white overflow-hidden" data-testid="calendar-view-switcher">
            {["month", "week"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`calendar-view-${v}`}
                className={`px-3 py-2 text-xs font-semibold capitalize ${view === v ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              >
                {v}
              </button>
            ))}
          </div>
          <select
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
            data-testid="calendar-vehicle-filter"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="all">All vehicles</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <div className="flex items-center rounded-md border border-slate-300 bg-white overflow-hidden">
            <button onClick={shiftPrev} data-testid="calendar-prev" className="px-2 py-2 hover:bg-slate-50"><ChevronLeft size={16} /></button>
            <span className="px-3 py-2 text-sm font-semibold min-w-[180px] text-center capitalize">{headerLabel}</span>
            <button onClick={shiftNext} data-testid="calendar-next" className="px-2 py-2 hover:bg-slate-50"><ChevronRight size={16} /></button>
          </div>
          <button onClick={goToday} data-testid="calendar-today" className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Today</button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="px-2 py-2 text-xs font-bold uppercase tracking-widest text-slate-500 text-center">{d}</div>
          ))}
        </div>
        <div className={view === "week" ? "grid grid-cols-1 sm:grid-cols-7" : "grid grid-cols-7"}>
          {grid.days.map((cell) => {
            const items = byDay.get(dayKey(cell.date)) || [];
            const isWeek = view === "week";
            const isOtherMonth = !isWeek && cell.date.getMonth() !== cursor.getMonth();
            const isToday = sameDay(cell.date, new Date());
            const key = dayKey(cell.date);
            const isDragOver = dragOverKey === key;
            const maxChips = isWeek ? 10 : 3;
            return (
              <div
                key={cell.date.toISOString()}
                data-testid={`calendar-day-${cell.date.getFullYear()}-${String(cell.date.getMonth() + 1).padStart(2, "0")}-${String(cell.date.getDate()).padStart(2, "0")}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverKey(key); }}
                onDragLeave={() => setDragOverKey((k) => (k === key ? null : k))}
                onDrop={(e) => onDropOnDay(cell.date, e)}
                className={`${isWeek ? "min-h-[420px]" : "min-h-[110px]"} border-r border-b border-slate-100 p-2 flex flex-col gap-1 transition-colors ${isOtherMonth ? "bg-slate-50/60" : ""} ${isDragOver ? "bg-sky-50 ring-1 ring-sky-300 ring-inset" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold ${isToday ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white" : isOtherMonth ? "text-slate-300" : "text-slate-600"}`}>
                    {isWeek
                      ? cell.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
                      : cell.date.getDate()}
                  </span>
                  {items.length > 0 && (
                    <span className="text-[10px] font-bold text-slate-400">{items.length}</span>
                  )}
                </div>
                <div className="flex flex-col gap-1 overflow-hidden">
                  {items.slice(0, maxChips).map((b) => (
                    <button
                      key={b.id + cell.date.toISOString()}
                      draggable
                      onDragStart={(e) => {
                        setDragId(b.id);
                        e.dataTransfer?.setData("text/plain", b.id);
                        if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => { setDragId(null); setDragOverKey(null); }}
                      onClick={() => setSelected(b)}
                      data-testid={`calendar-event-${b.id}`}
                      className={`text-left text-[11px] leading-tight rounded border px-1.5 py-1 truncate cursor-grab active:cursor-grabbing ${SERVICE_COLOR[b.service_type] || "bg-slate-100 text-slate-700 border-slate-200"}`}
                      title={`${b.customer_name} · ${b.service_type} · ${vname(b.vehicle_id)} · drag to reschedule`}
                    >
                      <span className="font-semibold">{b.customer_name}</span>
                      <span className="ml-1 opacity-70">· {vname(b.vehicle_id)}</span>
                      {isWeek && (
                        <span className="block text-[10px] text-slate-500 truncate">
                          {new Date(b.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {b.service_type}
                          {b.deposit_status === "paid" ? " · deposit paid" : ""}
                        </span>
                      )}
                    </button>
                  ))}
                  {items.length > maxChips && (
                    <span className="text-[10px] text-slate-500">+{items.length - maxChips} more</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600">
        {Object.entries(SERVICE_COLOR).map(([k, cls]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm border ${cls}`} /> {k}
          </span>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="calendar-event-modal" className="w-full max-w-md rounded-xl bg-white p-6 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-sky-700">{selected.service_type}</p>
                <h3 className="font-heading text-xl font-semibold mt-1">{selected.customer_name}</h3>
                {selected.deposit_status === "paid" && (
                  <span className="mt-1 inline-flex items-center rounded-full bg-emerald-100 text-emerald-900 ring-1 ring-inset ring-emerald-200 px-2 py-0.5 text-[10px] font-semibold">Deposit paid</span>
                )}
              </div>
              <StatusPill status={selected.status} />
            </div>
            <Row label="Email" value={<a href={`mailto:${selected.email}`} className="underline">{selected.email}</a>} />
            {selected.phone && (
              <Row
                label="Phone"
                value={(
                  <div>
                    <p>{formatPhoneForDisplay(selected.phone)}</p>
                    <AdminPhoneActions
                      phone={selected.phone}
                      preferredContactMethod={selected.preferred_contact_method}
                      testIdPrefix={`calendar-phone-${selected.id}`}
                    />
                  </div>
                )}
              />
            )}
            {selected.preferred_contact_method && <Row label="Contact method" value={selected.preferred_contact_method} />}
            <Row label="Start" value={new Date(selected.start_at).toLocaleString()} />
            <Row label="End" value={new Date(selected.end_at).toLocaleString()} />
            <Row label="Vehicle" value={vname(selected.vehicle_id)} />
            {selected.tour_name && <Row label="Tour" value={selected.tour_name} />}
            {selected.pickup && <Row label="Route" value={`${selected.pickup} → ${selected.dropoff}`} />}
            {selected.passengers && <Row label="Passengers" value={selected.passengers} />}
            {selected.notes && <Row label="Notes" value={selected.notes} />}
            <div className="pt-3 flex items-center justify-between">
              <button
                onClick={() => { setEmailTarget(selected); setSelected(null); }}
                data-testid="calendar-email-customer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Mail size={14} /> Email customer
              </button>
              <button onClick={() => setSelected(null)} className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Close</button>
            </div>
          </div>
        </div>
      )}
      {emailTarget && <EmailCustomerDialog booking={emailTarget} onClose={() => setEmailTarget(null)} />}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="w-24 shrink-0 text-xs font-bold uppercase tracking-widest text-slate-500">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}

function startOfMonth(d) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addMonths(d, n) {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}
function stripTime(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function buildMonthGrid(cursor) {
  const first = startOfMonth(cursor);
  // Monday-first week layout
  const offset = (first.getDay() + 6) % 7; // 0=Mon ... 6=Sun
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push({ date: stripTime(d) });
  }
  return { days };
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfWeek(d) {
  const x = stripTime(d);
  const offset = (x.getDay() + 6) % 7; // 0=Mon ... 6=Sun
  x.setDate(x.getDate() - offset);
  return x;
}
function buildWeekGrid(cursor) {
  const start = startOfWeek(cursor);
  const days = [];
  for (let i = 0; i < 7; i++) {
    days.push({ date: addDays(start, i) });
  }
  return { days };
}
function weekRangeLabel(cursor) {
  const s = startOfWeek(cursor);
  const e = addDays(s, 6);
  const fmt = (x) => x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(s)} – ${fmt(e)}, ${e.getFullYear()}`;
}
